"use client";

import { Button } from "@/components/rac/button";
import { cn } from "@/utils/cn";
import {
	ArrowLeft,
	ArrowRight,
	Building2,
	Heart,
	Home as HomeIcon,
	Loader2,
	TrendingUp,
	UserCheck,
	Users,
} from "lucide-react";
import { UserGroup } from "./types";

interface RelationshipDialogContentProps {
	step: "initial" | "group-selection";
	pendingRelationship: "buyer" | "seller" | null;
	groupsByRelationship: { buyer: UserGroup[]; seller: UserGroup[] };
	isLoading: boolean;
	selectedGroupId: string | null;
	selectedRelationship: "buyer" | "seller" | null;
	effectiveMode: "selection" | "guard";
	mode?: "selection" | "guard";
	requiredRelationship?: "buyer" | "seller";
	currentRelationship?: "buyer" | "seller";
	featureName?: string;
	description?: string;
	onClose: () => void;
	onBack: () => void;
	onGroupSelect: (groupId: string) => void;
	onRelationshipSwitch: (relationship: "buyer" | "seller") => void;
}

export function RelationshipDialogContent({
	step,
	pendingRelationship,
	groupsByRelationship,
	isLoading,
	selectedGroupId,
	effectiveMode,
	requiredRelationship,
	currentRelationship,
	featureName,
	description,
	onClose,
	onBack,
	onGroupSelect,
	onRelationshipSwitch,
}: RelationshipDialogContentProps) {
	if (step === "group-selection" && pendingRelationship) {
		// --- Group Selection UI (Integrated from GroupSelectionModal) ---
		const groups = groupsByRelationship[pendingRelationship] || [];
		const RelationshipIcon = pendingRelationship === "buyer" ? Heart : Building2;
		// Use static mapping for Tailwind classes (Suggestion applied)
		const relationshipStyles =
			pendingRelationship === "buyer"
				? { bg: "bg-blue-100", text: "text-blue-600" }
				: { bg: "bg-green-100", text: "text-green-600" };

		return (
			<div className="space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<button
						onClick={onBack}
						disabled={isLoading}
						className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
						aria-label="Back"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<span className="font-semibold text-gray-900">Select Group</span>
				</div>

				<div className="text-center mb-6">
					<div className="flex items-center justify-center gap-3 mb-2">
						<div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", relationshipStyles.bg)}>
							<RelationshipIcon className={cn("w-5 h-5", relationshipStyles.text)} />
						</div>
						<h2 className="text-xl font-bold text-primary-900 capitalize">{pendingRelationship} Groups</h2>
					</div>
					<p className="text-primary-700 text-sm max-w-sm mx-auto">
						Select which group you want to use for {pendingRelationship} mode.
					</p>
				</div>

				<div className="space-y-3 max-h-[40vh] overflow-y-auto mb-6">
					{groups.map(group => {
						const isSelected = selectedGroupId === group.id;
						const members = group.members || [];

						return (
							<div
								key={group.id}
								className={cn(
									"relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 text-left",
									isSelected
										? "border-primary-500 bg-primary-50 shadow-md"
										: "border-gray-200 hover:border-primary-300 hover:bg-gray-50",
									isLoading && "opacity-50 pointer-events-none",
								)}
								onClick={() => !isLoading && onGroupSelect(group.id)}
							>
								<div className="flex items-start gap-3">
									<div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
										<Users className="w-4 h-4 text-gray-600" />
									</div>
									<div className="flex-1 min-w-0">
										<h3 className={cn("font-medium text-sm mb-1", isSelected ? "text-primary-900" : "text-gray-900")}>
											{group.name}
										</h3>
										<p className="text-xs text-gray-500 capitalize">{group.type}</p>
										{members.length > 0 && (
											<div className="mt-2 text-xs text-gray-500">
												{members.length} member{members.length !== 1 ? "s" : ""}
											</div>
										)}
									</div>
								</div>
								{isLoading && selectedGroupId === group.id && (
									<div className="absolute inset-0 bg-white/80 rounded-lg flex items-center justify-center">
										<Loader2 className="w-5 h-5 animate-spin text-primary-600" />
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	// --- Initial Step UI ---

	if (effectiveMode === "selection") {
		// Selection Mode UI
		return (
			<div className="space-y-4">
				{/* Header */}
				<div className="bg-gradient-to-br from-primary-50 to-primary-100 px-6 py-8 text-center -mx-6 -mt-6 mb-6">
					<h2 className="text-2xl font-bold text-primary-900 mb-2">Choose Your Relationship</h2>
					<p className="text-primary-700 text-base">
						Tell us how you would like to use Settlin. You can change this anytime.
					</p>
				</div>

				{/* Buyer Option */}
				<div
					className={cn(
						"relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
						"border-gray-200 hover:border-primary-300 hover:bg-gray-50",
					)}
					onClick={() => !isLoading && onRelationshipSwitch("buyer")}
				>
					<div className="flex items-start gap-4">
						<div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
							<HomeIcon className="w-6 h-6 text-blue-600" />
						</div>
						<div className="flex-1">
							<h3 className="font-semibold text-gray-900 mb-1">I want to buy</h3>
							<p className="text-sm text-gray-600 mb-2">
								Find and purchase properties. Browse listings, schedule visits, and get assistance with buying.
							</p>
							<div className="flex items-center text-sm text-primary-600 font-medium">
								<span>Get started as a buyer</span>
								<ArrowRight className="w-4 h-4 ml-1" />
							</div>
						</div>
					</div>
				</div>

				{/* Seller Option */}
				<div
					className={cn(
						"relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200",
						"border-gray-200 hover:border-primary-300 hover:bg-gray-50",
					)}
					onClick={() => !isLoading && onRelationshipSwitch("seller")}
				>
					<div className="flex items-start gap-4">
						<div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
							<TrendingUp className="w-6 h-6 text-green-600" />
						</div>
						<div className="flex-1">
							<h3 className="font-semibold text-gray-900 mb-1">I want to sell</h3>
							<p className="text-sm text-gray-600 mb-2">
								List and sell properties. Post listings, manage inquiries, and get help with selling.
							</p>
							<div className="flex items-center text-sm text-primary-600 font-medium">
								<span>Get started as a seller</span>
								<ArrowRight className="w-4 h-4 ml-1" />
							</div>
						</div>
					</div>
				</div>

				<div className="pt-2">
					<Button variant="outline" onPress={onClose} isDisabled={isLoading} className="w-full">
						Skip for now
					</Button>
				</div>

				{isLoading && (
					<div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
						<Loader2 className="w-8 h-8 animate-spin text-primary-600" />
					</div>
				)}
			</div>
		);
	} else {
		// Guard Mode UI
		const relationshipConfig = {
			buyer: {
				label: "Buyer",
				description: "Find and purchase properties",
				icon: <UserCheck className="w-8 h-8 text-white" />,
				color: "from-blue-500 to-blue-600",
			},
			seller: {
				label: "Seller",
				description: "List and sell properties",
				icon: <Users className="w-8 h-8 text-white" />,
				color: "from-green-500 to-green-600",
			},
		};

		const required = relationshipConfig[requiredRelationship || "buyer"];
		const current = relationshipConfig[currentRelationship || "buyer"];
		const canSwitch = groupsByRelationship[requiredRelationship!]?.length > 0;

		return (
			<div className="space-y-6">
				{/* Header Icon */}
				<div className="text-center space-y-4">
					<div
						className={`w-16 h-16 mx-auto bg-gradient-to-br ${required.color} rounded-2xl flex items-center justify-center`}
					>
						{required.icon}
					</div>
					<div>
						<h2 className="text-xl font-bold text-gray-900 mb-2">
							{featureName} for {required.label}s
						</h2>
						{description && <p className="text-sm text-gray-600 leading-relaxed max-w-sm mx-auto">{description}</p>}
					</div>
				</div>

				{/* Status Cards */}
				<div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
					<div className="flex items-center gap-3 mb-3">
						<div className={`w-10 h-10 bg-gradient-to-br ${current.color} rounded-lg flex items-center justify-center`}>
							{current.icon}
						</div>
						<div>
							<p className="text-sm font-medium text-gray-900">Currently signed in as</p>
							<p className="text-lg font-semibold text-gray-800">{current.label}</p>
						</div>
					</div>
					<p className="text-xs text-gray-600">{current.description}</p>
				</div>

				<div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6">
					<div className="flex items-center gap-3 mb-4">
						<div
							className={`w-10 h-10 bg-gradient-to-br ${required.color} rounded-lg flex items-center justify-center`}
						>
							{required.icon}
						</div>
						<div>
							<p className="text-sm font-medium text-gray-700">This feature requires</p>
							<p className="text-lg font-semibold text-gray-900">{required.label} Access</p>
						</div>
					</div>

					{canSwitch ? (
						<div className="bg-white/50 rounded-lg p-3 border border-primary-200 mb-4">
							<p className="text-xs text-gray-600 mb-2">
								<strong>Good news!</strong> You can switch to {required.label} mode right now.
							</p>
						</div>
					) : (
						<div className="bg-white/50 rounded-lg p-3 border border-primary-200 mb-4">
							<p className="text-xs text-gray-600 mb-2">
								<strong>How to get access:</strong>
							</p>
							<ol className="text-xs text-gray-600 space-y-1">
								<li>1. Contact support to set up your {required.label} account</li>
								<li>2. Once approved, you&apos;ll be able to switch relationships</li>
								<li>3. Return to access this feature</li>
							</ol>
						</div>
					)}
				</div>

				{/* Actions */}
				<div className="pt-2 space-y-3">
					{canSwitch ? (
						<>
							<button
								type="button"
								onClick={() => onRelationshipSwitch(requiredRelationship!)}
								disabled={isLoading}
								className={cn(
									"w-full bg-primary text-white rounded-xl py-3 px-4 font-medium transition-colors",
									"hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed",
									"flex items-center justify-center gap-2",
								)}
							>
								{isLoading ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Switching...
									</>
								) : (
									<>Switch to {required.label} Mode</>
								)}
							</button>
							<button
								onClick={onClose}
								disabled={isLoading}
								className="w-full bg-gray-100 text-gray-700 rounded-xl py-3 px-4 font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
							>
								Maybe Later
							</button>
						</>
					) : (
						<button
							onClick={onClose}
							className="w-full bg-primary text-white rounded-xl py-3 px-4 font-medium hover:bg-primary/90 transition-colors"
						>
							Got it, I&apos;ll contact support
						</button>
					)}
				</div>
			</div>
		);
	}
}
